/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import getSystemList from "@eisinc/App/Handlers/XNG/getSystemList";
import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import { formatter } from "@eisinc/App/Utilities/PaymentCenterUtilities";
import Select, { SelectProps } from "@eisinc/MobiusOverrides/Select";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { COMMENT_REQUIRED_FIELD_MESSAGE, REASON_CODE_REQUIRED_FIELD_MESSAGE } from "@eisinc/XDL/constants";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormInputWrapper } from "@insite/mobius/FormField";
import Info from "@insite/mobius/Icons/Info";
import TextArea from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface PaymentOnAccountModel {
    value: string;
    reason: string;
    comment: string;
}

const mapDispatchToProps = {
    getSystemList,
};

interface OwnProps {
    onAddToSummary: (data: PaymentOnAccountModel) => void;
    paymentOnAccountValue?: PaymentOnAccountModel | undefined;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface PaymentOnAccountStyles {
    wrapper?: InjectableCss;
    textField?: TextFieldProps;
    label?: TypographyProps;
    tooltip?: TooltipPresentationProps;
    inputWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const styles: PaymentOnAccountStyles = {
    wrapper: {
        css: css`
            border: 1px solid ${color("common.ui4")};
            background: ${color("common.ui3")};
            padding: 12px;
            width: 100%;
            margin-bottom: 20px;
        `,
    },
    textField: {
        cssOverrides: {
            formField: css`
                &&&&& {
                    input {
                        text-align: right;
                        padding-left: 15px;
                        margin-bottom: 3px;
                    }

                    ${FormInputWrapper}::before {
                        content: "$";
                        position: absolute;
                        top: 8px;
                        left: 7px;
                    }
                }
            `,
        },
    },
    label: {
        css: css`
            font-weight: bold;
            line-height: 19px;
            margin-bottom: 4px;
        `,
    },
    tooltip: {
        iconProps: {
            src: Info,
            size: 16,
            color: "primary.main",
            css: css`
                position: relative;
                top: 3px;
                margin-left: 4px;
            `,
        },
    },
    inputWrapper: {
        css: css`
            margin-bottom: 3px;

            ${FormInputWrapper} {
                line-height: 10px;
            }

            textarea {
                resize: vertical;
            }
        `,
    },
    button: {
        css: css`
            width: 100%;
        `,
    },
};

const PaymentOnAccount: React.FC<Props> = ({ onAddToSummary, getSystemList, paymentOnAccountValue }) => {
    const [paymentValue, setPaymentValue] = useState("0.00");
    const [reasonCode, setReasonCode] = useState(paymentOnAccountValue?.reason || "");
    const [reasonCodeError, setReasonCodeError] = useState("");
    const [comment, setComment] = useState(paymentOnAccountValue?.comment || "");
    const [commentError, setCommentError] = useState("");
    const [hasValueGreaterThanZero, setHasValueGreaterThanZero] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [reasonCodes, setReasonCodes] = useState<GetSystemListValuesModel[]>([]);

    const toasterContext = useContext(ToasterContext);

    const handleChange = (e: any) => {
        const inputValue = e.target.value;
        if (/^\d*\.?\d{0,2}$/.test(inputValue) || inputValue === "") {
            setPaymentValue(inputValue);
            setHasValueGreaterThanZero(parseFloat(inputValue) > 0);
        }
    };

    const handleCodeChange: SelectProps["onChange"] = (event: any) => {
        const value = event.target.value;
        setReasonCodeError(value ? "" : REASON_CODE_REQUIRED_FIELD_MESSAGE);
        setReasonCode(value);
    };

    const handleCommentChange = (e: any) => {
        const value = e.target.value;
        setCommentError(value?.trim() ? "" : COMMENT_REQUIRED_FIELD_MESSAGE);
        setComment(value);
    };

    const handleCommentBlur = () => {
        setCommentError(comment ? "" : COMMENT_REQUIRED_FIELD_MESSAGE);
    };

    const isFormValid = () => {
        setReasonCodeError(reasonCode ? "" : REASON_CODE_REQUIRED_FIELD_MESSAGE);
        setCommentError(comment ? "" : COMMENT_REQUIRED_FIELD_MESSAGE);

        return !!reasonCode && !!comment;
    };

    const handleSubmit = () => {
        setIsSubmitted(true);
        if (!paymentValue || parseFloat(paymentValue) === 0) {
            removePayment();
            return;
        }
        if (isFormValid()) {
            const data = {
                value: paymentValue,
                reason: reasonCode,
                comment,
            } as PaymentOnAccountModel;
            onAddToSummary(data);
        }
    };

    const loadReasonCodes = () => {
        getSystemList({
            systemListName: "Custom_InvoicePaymentPayOnAccountReasonCodes",
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    setReasonCodes(resultProps.apiResult.result);
                }
            },
        });
    };

    useEffect(() => {
        loadReasonCodes();
    }, []);

    useEffect(() => {
        if (Number(paymentOnAccountValue?.value) > 0) {
            const formattedPaymentValue = Number(paymentOnAccountValue?.value)?.toLocaleString("en-US", {
                style: "decimal",
                minimumFractionDigits: 2,
            });
            setPaymentValue(formattedPaymentValue);
            setIsSubmitted(true);
        }
    }, [paymentOnAccountValue]);

    const removePayment = () => {
        const data = {} as PaymentOnAccountModel;
        onAddToSummary(data);
        setIsSubmitted(false);
        setPaymentValue("0.00");
        setReasonCode("");
        setComment("");
    };

    const getButtonLabel = () => {
        if (!isSubmitted) {
            return translate("Add To Summary");
        }
        if (!paymentValue || (isSubmitted && parseFloat(paymentValue) === 0)) {
            return translate("Remove Payment");
        }
        if (isSubmitted && parseFloat(paymentValue) !== 0) {
            return translate("Update Payment");
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h6" {...styles.label}>
                {translate("Payment On Account (Optional)")}
                <Tooltip {...styles.tooltip} text={siteMessage("Custom_Payment_On_Account_Tooltip") as string} />
            </Typography>
            <TextField
                id="paymentOnAccount"
                {...styles.textField}
                onChange={handleChange}
                value={paymentValue}
                maxLength={15}
            />

            {(hasValueGreaterThanZero || isSubmitted) && (
                <>
                    <StyledWrapper {...styles.inputWrapper}>
                        <Select
                            value={reasonCode}
                            disabled={false}
                            required={true}
                            onChange={handleCodeChange}
                            error={reasonCodeError}
                        >
                            <option value="">{translate("Select Reason")}</option>
                            {reasonCodes.map(code => (
                                <option key={code.name} value={code.name}>
                                    {code.description}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                    <StyledWrapper {...styles.inputWrapper}>
                        <TextArea
                            required={true}
                            onChange={handleCommentChange}
                            onBlur={handleCommentBlur}
                            error={commentError}
                            value={comment}
                            placeholder={translate("Example copy written by the user that goes on multiple lines")}
                            maxLength={4000}
                        />
                    </StyledWrapper>

                    <Button
                        variant="primary"
                        disabled={!reasonCode || !comment?.trim()}
                        onClick={handleSubmit}
                        {...styles.button}
                    >
                        {getButtonLabel()}
                    </Button>
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(PaymentOnAccount);
